import { handleRequest } from "../../axios";
import { axiosInstance } from "./JsdhNotifikaceService";
import {
    ADMIN_KONTROLY_TYP_DOKUMENTY_URL,
    ADMIN_KONTROLY_DOKUMENT_BY_ID_URL,
    ADMIN_KONTROLY_DOKUMENT_DOWNLOAD_URL,
    ADMIN_KONTROLY_POSUDEK_DOWNLOAD_URL,
    ADMIN_KONTROLY_POSUDEK_HROMADNY_URL,
} from "../url";
import type { KontrolaDokument, KontrolaDokumentUpdateRequest } from "../generated/models/KontrolaDokument";

// Vytáhne název souboru z hlavičky Content-Disposition.
const parseDownloadFilename = (cd: string | undefined, fallback: string): string => {
    if (!cd) return fallback;
    const star = cd.match(/filename\*=UTF-8''([^;]+)/i);
    if (star && star[1]) {
        try {
            return decodeURIComponent(star[1].replace(/"/g, '').trim());
        } catch {
            /* ignore */
        }
    }
    const plain = cd.match(/filename="?([^";]+)"?/i);
    return plain && plain[1] ? plain[1].trim() : fallback;
};

const listDokumenty = (typId: number) => {
    return handleRequest<KontrolaDokument[]>(() =>
        axiosInstance.get(ADMIN_KONTROLY_TYP_DOKUMENTY_URL(typId))
    );
};

// BE přijímá pouze "file" + volitelný "nazev" (@RequestParam — žádný popis).
const uploadDokument = (typId: number, file: File, nazev?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (nazev) formData.append('nazev', nazev);
    return handleRequest<KontrolaDokument>(() =>
        axiosInstance.post(ADMIN_KONTROLY_TYP_DOKUMENTY_URL(typId), formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
    );
};

const deleteDokument = (id: number) => {
    return handleRequest(() =>
        axiosInstance.delete(ADMIN_KONTROLY_DOKUMENT_BY_ID_URL(id))
    );
};

const updateDokumentMetadata = (id: number, data: KontrolaDokumentUpdateRequest) => {
    return handleRequest<KontrolaDokument>(() =>
        axiosInstance.put(ADMIN_KONTROLY_DOKUMENT_BY_ID_URL(id), data)
    );
};

const downloadDokument = async (id: number): Promise<{ blob: Blob; filename: string }> => {
    const res = await axiosInstance.get(ADMIN_KONTROLY_DOKUMENT_DOWNLOAD_URL(id), {
        responseType: 'blob',
    });
    return {
        blob: res.data,
        filename: parseDownloadFilename(res.headers['content-disposition'], `dokument-${id}.pdf`),
    };
};

// Stažení lékařského posudku (bez hesla, admin přístup)
const downloadPosudek = async (userId: number): Promise<{ blob: Blob; filename: string }> => {
    const res = await axiosInstance.get(ADMIN_KONTROLY_POSUDEK_DOWNLOAD_URL(userId), {
        responseType: 'blob',
    });
    return {
        blob: res.data,
        filename: parseDownloadFilename(res.headers['content-disposition'], `posudek-${userId}.pdf`),
    };
};

// Hromadný posudek — POST + blob (nový pattern; response je PDF, ne JSON)
const downloadHromadnyPosudek = async (userIds: number[]): Promise<{ blob: Blob; filename: string }> => {
    const res = await axiosInstance.post(
        ADMIN_KONTROLY_POSUDEK_HROMADNY_URL,
        { userIds },
        { responseType: 'blob' }
    );
    return {
        blob: res.data,
        filename: parseDownloadFilename(res.headers['content-disposition'], 'posudky-hromadny.pdf'),
    };
};

const KontrolyDokumentService = {
    listDokumenty,
    uploadDokument,
    deleteDokument,
    updateDokumentMetadata,
    downloadDokument,
    downloadPosudek,
    downloadHromadnyPosudek,
};

export default KontrolyDokumentService;
